import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Calendar,
  Plus,
  Filter,
  Settings,
  LayoutGrid,
  List,
  Users, // Added for Partner Tab
} from "lucide-react";

// --- Modular Imports ---
import { DATES, INITIAL_TASKS } from "./data/constants";
import TaskCard from "./components/TaskCard";
import SettingsPanel from "./components/SettingsPanel";
import WeekSummary from "./components/WeekSummary";
import MelyssaDashboard from "./components/PartnerDashboard";
import ChristmasTimeline from "./components/ChristmasTimeline"; 
import ChristmasEveTimeline from "./components/ChristmasEveTimeline"; 

// --- Modals ---
import CoffeeMeetingModal from "./components/CoffeeMeetingModal";
import LifeCoachModal from "./components/LifeCoachMode";

// --- Control Cards ---
import ProgramLaunchCard from "./components/ProgramLaunchCard";
import GiftControlCard from "./components/GiftControlCard";
import FoodControlCard from "./components/FoodControlCard";
import StockingStufferCard from "./components/StockingStufferCard";
import DecadeProtocolCard from "./components/DecadeProtocolCard";

// --- Triggers & Events ---
import {
  CoffeeMeetingTrigger,
  LifeCoachTrigger,
  CleanersTrigger,
} from "./components/EventTriggers";
import {
  GolfSimCard,
  AlumniGameCard,
  EveEveCard,
} from "./components/SocialEvents";

export default function HolidayOS() {
  // --- State ---
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [currentDay, setCurrentDay] = useState(DATES[0].id);
  const [filterOwner, setFilterOwner] = useState("all");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [names, setNames] = useState({ you: "Me", partner: "Partner" });

  // View State (Week vs Day vs Partner)
  const [viewMode, setViewMode] = useState("week");

  // Modal States
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showCoachModal, setShowCoachModal] = useState(false);

  // Persistence
  useEffect(() => {
    const saved = localStorage.getItem("holiday-os-tasks");
    const savedNames = localStorage.getItem("holiday-os-names");
    if (saved) setTasks(JSON.parse(saved));
    if (savedNames) setNames(JSON.parse(savedNames));
  }, []);

  useEffect(() => {
    localStorage.setItem("holiday-os-tasks", JSON.stringify(tasks));
    localStorage.setItem("holiday-os-names", JSON.stringify(names));
  }, [tasks, names]);

  // --- Handlers ---
  const addTask = () => {
    const newTask = {
      id: Date.now().toString(),
      day: currentDay,
      title: "New Responsibility",
      category: "house",
      owner: "you",
      timeWindow: "Anytime",
      doneLooksLike: "Describe exact outcome...",
      status: "todo",
      priority: "medium",
      notes: "",
    };
    setTasks([...tasks, newTask]);
  };

  const updateTask = (id, field, value) => {
    setTasks(tasks.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const deleteTask = (id) => {
    if (window.confirm("Remove this task?")) {
      setTasks(tasks.filter((t) => t.id !== id));
    }
  };

  const resetData = () => {
    if (window.confirm("Reset all data to defaults?")) {
      setTasks(INITIAL_TASKS);
      localStorage.removeItem("holiday-os-tasks");
    }
  };

  // --- Logic ---
  const daysTasks = tasks.filter((t) => t.day === currentDay);
  const filteredTasks = daysTasks.filter((t) => {
    if (filterOwner === "all") return true;
    return t.owner === filterOwner;
  });

  const stats = {
    total: daysTasks.length,
    done: daysTasks.filter((t) => t.status === "done").length,
    blocked: daysTasks.filter((t) => t.status === "blocked").length,
  };
  const percent =
    stats.total === 0 ? 0 : Math.round((stats.done / stats.total) * 100);

  // --- Render ---
  return (
    <div className="min-h-screen bg-[#F5F5F4] text-stone-800 font-sans pb-20 selection:bg-[#EAF4EF] selection:text-[#1F6F54]">
      {/* HEADER */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-stone-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center py-5">
            {/* UPDATED TITLE */}
            <h1 className="text-xl font-bold text-[#1F6F54] tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-stone-400" />
              Christmas Week - Prep
            </h1>

            {/* VIEW TOGGLE */}
            <div className="flex bg-stone-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode("week")}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                  viewMode === "week"
                    ? "bg-white text-[#1F6F54] shadow-sm"
                    : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <LayoutGrid className="w-3 h-3" /> Week
              </button>
              <button
                onClick={() => setViewMode("day")}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                  viewMode === "day"
                    ? "bg-white text-[#1F6F54] shadow-sm"
                    : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <List className="w-3 h-3" /> Day
              </button>
              <button
                onClick={() => setViewMode("partner")}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                  viewMode === "partner"
                    ? "bg-white text-rose-500 shadow-sm"
                    : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <Users className="w-3 h-3" /> Melyssa
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  setFilterOwner(
                    filterOwner === "all"
                      ? "you"
                      : filterOwner === "you"
                      ? "partner"
                      : "all"
                  )
                }
                className={`p-2 rounded-full transition-colors ${
                  filterOwner !== "all"
                    ? "bg-[#EAF4EF] text-[#1F6F54]"
                    : "text-stone-400 hover:bg-stone-100"
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                className="p-2 text-stone-400 hover:bg-stone-100 rounded-full transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Day Tabs (Only visible in Day Mode) */}
          {viewMode === "day" && (
            <div className="flex gap-1 overflow-x-auto pb-0 scrollbar-hide">
              {DATES.map((date) => (
                <button
                  key={date.id}
                  onClick={() => setCurrentDay(date.id)}
                  className={`
                    flex-1 py-3 px-2 text-sm font-medium border-b-2 transition-all whitespace-nowrap
                    ${
                      currentDay === date.id
                        ? "border-[#1F6F54] text-[#1F6F54] bg-[#EAF4EF]"
                        : "border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50"
                    }
                  `}
                >
                  {date.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <SettingsPanel
        isOpen={isSettingsOpen}
        names={names}
        setNames={setNames}
        onReset={resetData}
      />

      {/* MAIN CONTENT AREA */}
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        
        {/* --- VIEW 1: WEEK SUMMARY --- */}
        {viewMode === "week" && (
          <WeekSummary
            onNavigate={(dayId) => {
              setCurrentDay(dayId);
              setViewMode("day");
            }}
          />
        )}

        {/* --- VIEW 2: PARTNER DASHBOARD --- */}
        {viewMode === "partner" && <MelyssaDashboard />}

        {/* --- VIEW 3: DAY EXECUTION --- */}
        {viewMode === "day" && (
          <>
            {/* Reality Check Bar */}
            <div className="bg-white rounded-lg p-5 shadow-sm border border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-base font-semibold text-stone-800">
                  {DATES.find((d) => d.id === currentDay)?.full} Snapshot
                </div>
                <div className="text-xs text-stone-500 mt-0.5 font-medium">
                  {stats.total === 0
                    ? "Quiet day."
                    : `${stats.done} of ${stats.total} settled`}
                </div>
              </div>
              <div className="flex items-center gap-4">
                {stats.blocked > 0 && (
                  <div className="flex items-center gap-1.5 text-rose-700 text-xs font-bold bg-rose-50 px-2 py-1 rounded">
                    <AlertCircle className="w-3 h-3" /> {stats.blocked} Blocked
                  </div>
                )}
                <div className="w-32 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-700 ease-out ${
                      percent === 100 ? "bg-amber-400" : "bg-[#1F6F54]"
                    }`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* --- WORK & LOGISTICS (Top of Stack) --- */}

            {(currentDay === "2025-12-22" || currentDay === "2025-12-23") && (
              <ProgramLaunchCard />
            )}

            {/* --- STRATEGIC SIDE PROJECT (Monday Only) --- */}
            {currentDay === "2025-12-22" && <DecadeProtocolCard />}

            {(currentDay === "2025-12-22" ||
              currentDay === "2025-12-23" ||
              currentDay === "2025-12-24") && <FoodControlCard />}

            {(currentDay === "2025-12-22" ||
              currentDay === "2025-12-23" ||
              currentDay === "2025-12-24") && <GiftControlCard />}

            {(currentDay === "2025-12-22" ||
              currentDay === "2025-12-23" ||
              currentDay === "2025-12-24") && <StockingStufferCard />}

            {/* --- HARD SCHEDULE EVENTS --- */}

            <CleanersTrigger currentDay={currentDay} />

            <LifeCoachTrigger
              currentDay={currentDay}
              onClick={() => setShowCoachModal(true)}
            />

            <CoffeeMeetingTrigger
              currentDay={currentDay}
              onClick={() => setShowMeetingModal(true)}
            />

            <AlumniGameCard currentDay={currentDay} />

            {/* --- SOCIAL ANCHORS --- */}

            <EveEveCard currentDay={currentDay} />
            <GolfSimCard currentDay={currentDay} />

            {/* --- TACTICAL TASK LIST & TIMELINES --- */}

            {currentDay === "2025-12-25" ? (
              /* Christmas Day Special Timeline */
              <ChristmasTimeline />
            ) : currentDay === "2025-12-24" ? (
              /* Christmas Eve Flow + Logistics */
              <>
                 <ChristmasEveTimeline />
                 
                 {filteredTasks.length > 0 && (
                   <div className="mt-8 border-t border-stone-200 pt-8">
                      <div className="text-center text-xs text-stone-400 uppercase tracking-widest mb-4">
                         Critical Logistics
                      </div>
                      <div className="space-y-4">
                        {filteredTasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            names={names}
                            onUpdate={(field, value) => updateTask(task.id, field, value)}
                            onDelete={deleteTask}
                          />
                        ))}
                      </div>
                   </div>
                 )}
              </>
            ) : (
              /* Standard Task List for Non-Christmas Days (22, 23) */
              <>
                {filterOwner !== "all" && (
                  <div className="text-center text-xs text-stone-400 uppercase tracking-widest">
                    Lane:{" "}
                    <span className="font-bold text-[#1F6F54]">
                      {filterOwner === "you" ? names.you : names.partner}
                    </span>
                  </div>
                )}

                <div className="space-y-4">
                  {filteredTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      names={names}
                      onUpdate={(field, value) => updateTask(task.id, field, value)}
                      onDelete={deleteTask}
                    />
                  ))}

                  {filteredTasks.length === 0 && (
                    <div className="text-center py-16">
                      <div className="inline-block p-4 rounded-full bg-stone-100 text-stone-300 mb-3">
                        <CheckCircle2 className="w-8 h-8" />
                      </div>
                      <p className="text-stone-500 text-sm font-medium">
                        The fairway is clear.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={addTask}
                    className="w-full py-5 border-2 border-dashed border-[#1F6F54]/30 rounded-xl text-[#1F6F54] hover:border-[#1F6F54] hover:bg-[#EAF4EF] transition-all flex items-center justify-center gap-2 font-medium opacity-70 hover:opacity-100"
                  >
                    <Plus className="w-5 h-5" />
                    Add Responsibility
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <footer className="text-center py-8 text-stone-300 text-xs tracking-wide">
        <p>Quietly Organized.</p>
      </footer>

      {/* --- MODALS --- */}
      {showMeetingModal && (
        <CoffeeMeetingModal onClose={() => setShowMeetingModal(false)} />
      )}
      {showCoachModal && (
        <LifeCoachModal onClose={() => setShowCoachModal(false)} />
      )}
    </div>
  );
}